import React, { useState } from 'react';
import { Typography, Card, Tag, Divider, Alert, Space, Collapse, Button, message } from 'antd';
import { CopyOutlined, ApiOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthContext';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

function ApiDocumentation() {
  const { user } = useAuth();
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = (text, itemName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(itemName);
      message.success('Copied to clipboard!');
      setTimeout(() => setCopiedItem(null), 2000);
    });
  };

  const CodeBlock = ({ children, copiable = false, copyId = '' }) => (
    <div style={{ position: 'relative' }}>
      <pre style={{
        backgroundColor: '#f5f5f5',
        padding: '16px',
        borderRadius: '4px',
        overflow: 'auto',
        fontSize: '13px',
        border: '1px solid #d9d9d9'
      }}>
        <code>{children}</code>
      </pre>
      {copiable && (
        <Button
          type="text"
          size="small"
          icon={copiedItem === copyId ? <CheckCircleOutlined /> : <CopyOutlined />}
          onClick={() => copyToClipboard(children, copyId)}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)'
          }}
        >
          {copiedItem === copyId ? 'Copied!' : 'Copy'}
        </Button>
      )}
    </div>
  );

  const baseUrl = 'https://europe-west2-ovaielo-192521.cloudfunctions.net';

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={2}>
            <ApiOutlined /> API Documentation
          </Title>
          <Paragraph>
            Access your agricultural planning data programmatically through our RESTful API.
            All endpoints require authentication using Firebase ID tokens.
          </Paragraph>
        </div>

        {/* Authentication Section */}
        <Card title={<><LockOutlined /> Authentication</>} size="small">
          <Paragraph>
            All API requests must include a valid Firebase ID token in the Authorization header.
          </Paragraph>

          <Text strong>Header Format:</Text>
          <CodeBlock copiable copyId="auth-header">
{`Authorization: Bearer <YOUR_FIREBASE_ID_TOKEN>`}
          </CodeBlock>

          {user && (
            <Alert
              message="You are currently logged in"
              description={`User ID: ${user.uid}`}
              type="info"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}

          <Divider />

          <Text strong>Getting Your ID Token (JavaScript):</Text>
          <CodeBlock copiable copyId="get-token">
{`import { getAuth } from 'firebase/auth';

const auth = getAuth();
const user = auth.currentUser;

if (user) {
  const idToken = await user.getIdToken();
  // Use this token in your API requests
}`}
          </CodeBlock>
        </Card>

        {/* Base URL Section */}
        <Card title="Base URLs" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Tag color="green">Production</Tag>
              <Text code>{baseUrl}/api</Text>
            </div>
            <div>
              <Tag color="orange">Staging</Tag>
              <Text code>{baseUrl}/staging</Text>
            </div>
            <div>
              <Tag color="blue">Development</Tag>
              <Text code>{baseUrl}/dev</Text>
            </div>
          </Space>
        </Card>

        {/* API Endpoints */}
        <Card title="API Endpoints" size="small">
          <Collapse accordion>
            {/* Crop Areas - List All */}
            <Panel
              header={
                <Space>
                  <Tag color="green">GET</Tag>
                  <Text code>/cropareas</Text>
                  <Text type="secondary">Get all crop areas</Text>
                </Space>
              }
              key="1"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Retrieves all crop areas for the authenticated user. Returns area boundaries,
                    plant groups, and calculated metrics.
                  </Paragraph>
                </div>

                <Divider />

                <Text strong>Request Example:</Text>
                <CodeBlock copiable copyId="crop-areas-request">
{`curl -X GET \\
  '${baseUrl}/api/cropareas' \\
  -H 'Authorization: Bearer YOUR_FIREBASE_ID_TOKEN' \\
  -H 'Content-Type: application/json'`}
                </CodeBlock>

                <Divider />

                <Text strong>Response (200 OK):</Text>
                <CodeBlock copiable copyId="crop-areas-response">
{`{
  "cropAreas": [
    {
      "id": "crop-123",
      "name": "North Field",
      "coordinates": [
        { "lat": 59.3293, "lng": 18.0686 },
        { "lat": 59.3295, "lng": 18.0690 },
        { "lat": 59.3291, "lng": 18.0692 }
      ],
      "plantGroupId": "group-456",
      "area": 2500.50
    }
  ],
  "count": 1,
  "lastUpdated": "2025-01-20T12:30:00.000Z"
}`}
                </CodeBlock>

                <Divider />

                <Text strong>Response Fields:</Text>
                <ul>
                  <li><Text code>id</Text> - Unique identifier for the crop area</li>
                  <li><Text code>name</Text> - User-defined name</li>
                  <li><Text code>coordinates</Text> - Array of lat/lng points defining the boundary</li>
                  <li><Text code>plantGroupId</Text> - Associated plant group identifier</li>
                  <li><Text code>area</Text> - Calculated area in square meters</li>
                </ul>
              </Space>
            </Panel>

            {/* Crop Areas - Get Single */}
            <Panel
              header={
                <Space>
                  <Tag color="green">GET</Tag>
                  <Text code>/cropareas/:id</Text>
                  <Text type="secondary">Get specific crop area</Text>
                </Space>
              }
              key="2"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Retrieves a specific crop area by ID.
                  </Paragraph>
                </div>

                <Divider />

                <Text strong>Request Example:</Text>
                <CodeBlock copiable copyId="crop-area-single-request">
{`curl -X GET \\
  '${baseUrl}/api/cropareas/crop-123' \\
  -H 'Authorization: Bearer YOUR_FIREBASE_ID_TOKEN' \\
  -H 'Content-Type: application/json'`}
                </CodeBlock>

                <Divider />

                <Text strong>Response (200 OK):</Text>
                <CodeBlock copiable copyId="crop-area-single-response">
{`{
  "cropArea": {
    "id": "crop-123",
    "name": "North Field",
    "coordinates": [...],
    "plantGroupId": "group-456",
    "area": 2500.50
  },
  "lastUpdated": "2025-01-20T12:30:00.000Z"
}`}
                </CodeBlock>

                <Divider />

                <Text strong>Error Response (404):</Text>
                <CodeBlock>
{`{
  "error": "Crop area not found"
}`}
                </CodeBlock>
              </Space>
            </Panel>

            {/* Plant Groups - List All */}
            <Panel
              header={
                <Space>
                  <Tag color="green">GET</Tag>
                  <Text code>/plantgroups</Text>
                  <Text type="secondary">Get all plant groups</Text>
                </Space>
              }
              key="3"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Retrieves all plant group configurations for the authenticated user.
                  </Paragraph>
                </div>

                <Divider />

                <Text strong>Request Example:</Text>
                <CodeBlock copiable copyId="plant-groups-request">
{`curl -X GET \\
  '${baseUrl}/api/plantgroups' \\
  -H 'Authorization: Bearer YOUR_FIREBASE_ID_TOKEN' \\
  -H 'Content-Type: application/json'`}
                </CodeBlock>

                <Divider />

                <Text strong>Response (200 OK):</Text>
                <CodeBlock copiable copyId="plant-groups-response">
{`{
  "plantGroups": [
    {
      "id": "group-456",
      "name": "Tomatoes",
      "color": "#ff0000",
      "plantType": "Tomato",
      "slotId": "crop-123",
      "rowSpacing": 1.0,
      "colSpacing": 0.5,
      "rowMargin": 0.2,
      "colMargin": 0.2,
      "numRows": 10,
      "numCols": 20,
      "notes": "Cherry tomatoes variety"
    }
  ],
  "count": 1,
  "lastUpdated": "2025-01-20T12:30:00.000Z"
}`}
                </CodeBlock>

                <Divider />

                <Text strong>Response Fields:</Text>
                <ul>
                  <li><Text code>id</Text> - Unique identifier for the plant group</li>
                  <li><Text code>name</Text> - Group name</li>
                  <li><Text code>color</Text> - Display color (hex format)</li>
                  <li><Text code>plantType</Text> - Type of plant</li>
                  <li><Text code>slotId</Text> - Associated crop area ID</li>
                  <li><Text code>rowSpacing</Text> - Distance between rows (meters)</li>
                  <li><Text code>colSpacing</Text> - Distance between columns (meters)</li>
                  <li><Text code>rowMargin</Text> - Offset from edge (rows)</li>
                  <li><Text code>colMargin</Text> - Offset from edge (columns)</li>
                  <li><Text code>numRows</Text> - Number of rows</li>
                  <li><Text code>numCols</Text> - Number of columns</li>
                </ul>
              </Space>
            </Panel>

            {/* Plant Groups - Get Single with Positions */}
            <Panel
              header={
                <Space>
                  <Tag color="green">GET</Tag>
                  <Text code>/plantgroups/:id</Text>
                  <Text type="secondary">Get plant group with calculated positions</Text>
                </Space>
              }
              key="4"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph>
                    Retrieves a specific plant group with calculated GPS positions for each plant.
                    Uses advanced algorithms to position plants within the crop area boundary.
                  </Paragraph>
                </div>

                <Divider />

                <Text strong>Request Example:</Text>
                <CodeBlock copiable copyId="plant-group-single-request">
{`curl -X GET \\
  '${baseUrl}/api/plantgroups/group-456' \\
  -H 'Authorization: Bearer YOUR_FIREBASE_ID_TOKEN' \\
  -H 'Content-Type: application/json'`}
                </CodeBlock>

                <Divider />

                <Text strong>Response (200 OK):</Text>
                <CodeBlock copiable copyId="plant-group-single-response">
{`{
  "plantGroup": {
    "id": "group-456",
    "name": "Tomatoes",
    "color": "#ff0000",
    "plantType": "Tomato",
    "slotId": "crop-123",
    "rowSpacing": 1.0,
    "colSpacing": 0.5,
    "rowMargin": 0.2,
    "colMargin": 0.2,
    "numRows": 10,
    "numCols": 20,
    "notes": "Cherry tomatoes variety",
    "plantPositions": [
      { "lat": 59.32931, "lng": 18.06862 },
      { "lat": 59.32932, "lng": 18.06863 },
      ...
    ],
    "totalPlants": 200
  },
  "lastUpdated": "2025-01-20T12:30:00.000Z"
}`}
                </CodeBlock>

                <Divider />

                <Text strong>Additional Fields:</Text>
                <ul>
                  <li><Text code>plantPositions</Text> - Array of calculated GPS coordinates for each plant</li>
                  <li><Text code>totalPlants</Text> - Total number of plant positions calculated</li>
                </ul>

                <Alert
                  message="Position Calculation"
                  description="Plant positions are calculated using the Haversine formula for accurate geodesic distance calculations, taking into account the Earth's curvature."
                  type="info"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              </Space>
            </Panel>
          </Collapse>
        </Card>

        {/* Common Response Codes */}
        <Card title="HTTP Response Codes" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Tag color="success">200 OK</Tag>
              <Text>Request successful</Text>
            </div>
            <div>
              <Tag color="error">401 Unauthorized</Tag>
              <Text>Missing or invalid authentication token</Text>
            </div>
            <div>
              <Tag color="error">404 Not Found</Tag>
              <Text>Resource not found or user has no data</Text>
            </div>
            <div>
              <Tag color="error">405 Method Not Allowed</Tag>
              <Text>HTTP method not supported for this endpoint</Text>
            </div>
            <div>
              <Tag color="error">500 Internal Server Error</Tag>
              <Text>Server error occurred</Text>
            </div>
          </Space>
        </Card>

        {/* Rate Limits and Best Practices */}
        <Card title="Best Practices" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              <ul>
                <li>Always include the <Text code>Authorization</Text> header with a valid Firebase ID token</li>
                <li>ID tokens expire after 1 hour - refresh them regularly</li>
                <li>Check the <Text code>API-Version</Text> response header to ensure compatibility</li>
                <li>Cache responses when appropriate to reduce API calls</li>
                <li>Handle errors gracefully and check for specific error messages</li>
                <li>Use the appropriate environment (production/staging/development) for testing</li>
              </ul>
            </Paragraph>
          </Space>
        </Card>

        {/* Versioning */}
        <Card title="API Versioning" size="small">
          <Paragraph>
            The current API version is <Tag color="blue">v1</Tag>. All responses include an
            <Text code> API-Version</Text> header.
          </Paragraph>
          <Paragraph>
            Future versions will maintain backwards compatibility. Breaking changes will result
            in a new version endpoint.
          </Paragraph>
        </Card>

        {/* Example Integration */}
        <Card title="Example Integration" size="small">
          <Text strong>JavaScript/Node.js Example:</Text>
          <CodeBlock copiable copyId="js-example">
{`const fetchCropAreas = async (idToken) => {
  try {
    const response = await fetch(
      '${baseUrl}/api/cropareas',
      {
        method: 'GET',
        headers: {
          'Authorization': \`Bearer \${idToken}\`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const data = await response.json();
    console.log('Crop Areas:', data.cropAreas);
    console.log('Count:', data.count);
    return data;
  } catch (error) {
    console.error('Error fetching crop areas:', error);
    throw error;
  }
};

// Usage
const auth = getAuth();
const user = auth.currentUser;
if (user) {
  const token = await user.getIdToken();
  const cropData = await fetchCropAreas(token);
}`}
          </CodeBlock>

          <Divider />

          <Text strong>Python Example:</Text>
          <CodeBlock copiable copyId="python-example">
{`import requests

def fetch_crop_areas(id_token):
    url = '${baseUrl}/api/cropareas'
    headers = {
        'Authorization': f'Bearer {id_token}',
        'Content-Type': 'application/json'
    }

    response = requests.get(url, headers=headers)
    response.raise_for_status()

    data = response.json()
    print(f"Crop Areas: {data['cropAreas']}")
    print(f"Count: {data['count']}")
    return data

# Usage
# id_token = get_firebase_token()  # Your token retrieval method
# crop_data = fetch_crop_areas(id_token)`}
          </CodeBlock>
        </Card>

        {/* Footer Note */}
        <Alert
          message="Need Help?"
          description="For additional support or to report issues with the API, please contact the development team or file an issue on the project repository."
          type="info"
          showIcon
        />
      </Space>
    </div>
  );
}

export default ApiDocumentation;
